import { Box, Button, TextField, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import LoginIcon from "@mui/icons-material/Login";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import Alert from "@mui/material/Alert";
import { PrimaryButton } from "../../../components/general/buttons/PrimaryButton";
import { useAuth } from "../../../context/AuthProvider/useAuth";
import { UserRole } from "../../../types/types";
import { loginSchema } from "../../../utils/yupSchemas";
import {
  CURRENT_SUBDOMAIN,
  DEMANDORIA_SUBDOMAINS,
} from "../../../utils/stringUtils";
import { sharedStyles } from "@/styles/shared";
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";
import GoogleLoginComercialOptionModal from "./GoogleLoginComercialOptionModal";
import { useTranslation } from "react-i18next";

const Login = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const navigate = useNavigate();

  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const {
    register,
    handleSubmit: onSubmit,
    formState: { errors },
  } = useForm({ resolver: yupResolver(loginSchema) });

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [requestErrorFlag, setRequestErrorFlag] = useState<boolean>(false);

  const [openComercialLoginOptionModal, setOpenComercialLoginOptionModal] =
    useState<string | null>(null);

  useEffect(() => {
    if (auth.user.role && auth.isLoggedIn) {
      switch (auth.user.role) {
        case UserRole.CLIENT:
          navigate("/home");
          break;
        case UserRole.COMERCIAL:
          navigate("/appointments");
          break;
        default:
          navigate("/home");
          break;
      }
    }
  }, [auth]);

  const handleSubmit = async () => {
    const email = emailRef.current.value;
    const password = passwordRef.current.value;

    setRequestErrorFlag(false);
    setIsLoading(true);

    try {
      await auth.authenticate(email, password);
    } catch (error) {
      setRequestErrorFlag(true);
      setIsLoading(false);
      console.warn("Error on user login: ", error);
    }
  };

  const handleSubmitGuestLogin = async () => {
    setRequestErrorFlag(false);

    try {
      await auth.authenticateAsGuest();
    } catch (error) {
      setRequestErrorFlag(true);
      console.warn("Error on guest login: ", error);
    }
  };

  useEffect(() => {
    setRequestErrorFlag(false);
  }, [emailRef.current?.value, passwordRef.current?.value]);

  return (
    <Box
      sx={{
        ...sharedStyles.generalContainer,
        ...sharedStyles.form,
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <GoogleLoginComercialOptionModal
        credential={openComercialLoginOptionModal}
        setOpen={setOpenComercialLoginOptionModal}
      />

      <Box
        sx={{
          width: "100%",
          maxWidth: "600px",
          ...sharedStyles.form,
          paddingX: { xs: 3, md: 3, lg: 10 },
        }}
      >
        <Box sx={{ ...sharedStyles.verticalContent, alignItems: "center" }}>
          <Typography variant="h4" fontWeight="bold" textAlign="center">
            {CURRENT_SUBDOMAIN === DEMANDORIA_SUBDOMAINS[0]
              ? t("login.titles.beauty")
              : t("login.titles.pet")}
          </Typography>

          <Typography>{t("login.titles.connect")}</Typography>
        </Box>

        <form onSubmit={onSubmit(handleSubmit)} style={sharedStyles.form}>
          <TextField
            label={t("login.labels.email")}
            type="email"
            inputRef={emailRef}
            {...register("email")}
            error={!!errors.email || requestErrorFlag}
            helperText={errors.email?.message}
            fullWidth
          />

          <Box sx={sharedStyles.verticalContent}>
            <TextField
              label={t("login.labels.password")}
              type="password"
              inputRef={passwordRef}
              {...register("password")}
              error={!!errors.password || requestErrorFlag}
              helperText={errors.password?.message}
              fullWidth
            />
            <Typography
              onClick={() => navigate("/password-recovery")}
              component={"span"}
              variant="body2"
              sx={{
                alignSelf: "flex-end",
                fontWeight: "bold",
                color: "text.secondary",
                cursor: "pointer",
              }}
            >
                {t("login.actions.forgotPassword")}
            </Typography>
          </Box>

        <Box sx={sharedStyles.verticalContent}>
            <PrimaryButton
            label={t("login.actions.enter")}
              icon={<LoginIcon />}
              isLoading={isLoading}
              styles={sharedStyles.secondaryColor}
            />

            {requestErrorFlag && (
              <Alert
                severity="error"
                sx={{ width: "100%", padding: "0px 10px" }}
              >
                {t("login.messages.invalidCredentials")}
              </Alert>
            )}

            <Box
              sx={{
                ...sharedStyles.horizontalContent,
                width: "100%",
                justifyContent: "space-between",

                "@media(max-width:600px)": {
                  flexDirection: "column",
                },
              }}
            >
              <Button
                sx={{ textTransform: "none", p: 0 }}
                onClick={handleSubmitGuestLogin}
              >
                 {t("login.actions.enterAsGuest")}
              </Button>

              <Typography sx={{ alignSelf: "center" }}>
                  {t("login.messages.newUser")}{" "}
                <Button
                  sx={{ textTransform: "none", p: 0 }}
                  onClick={() => navigate("/register")}
                >
                  {t("login.actions.register")}
                </Button>
              </Typography>
            </Box>
          </Box>
        </form>

        <Box
          sx={{ ...sharedStyles.horizontalContent, justifyContent: "center" }}
        >
          <GoogleOAuthProvider
            clientId={import.meta.env.VITE_GOOGLE_THIRD_PARTY_LOGIN_CLIENT_ID}
          >
            <GoogleLogin
              onSuccess={async (credentialResponse) => {
                const accessToken = credentialResponse.credential;

                try {
                  await auth.authenticateWithGoogle(accessToken);
                } catch (error) {
                  setOpenComercialLoginOptionModal(accessToken);
                }
              }}
              onError={() => alert(t("login.messages.googleLoginFail"))}
              useOneTap
            />
          </GoogleOAuthProvider>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
